// Domain types used across the systems-shell. Kept minimal on purpose —
// this is a scaffold, not a full factory-run contract.

export type FactoryStage =
  | 'intake'
  | 'discovery'
  | 'oracle'
  | 'logos'
  | 'genesis'
  | 'complete';

export const STAGE_LABELS: Record<FactoryStage, string> = {
  intake: 'Intake',
  discovery: 'Discovery',
  oracle: 'Oracle',
  logos: 'Logos',
  genesis: 'Genesis',
  complete: 'Complete',
};

export const STAGE_ORDER: FactoryStage[] = [
  'intake',
  'discovery',
  'oracle',
  'logos',
  'genesis',
  'complete',
];

// Raw status values written by factory services on factory_runs.status.
// Mirrors what rta-chat-v2/lib/factoryRuns.ts tracks, extended with the
// three awaiting-approval variants the orchestrator emits between stages.
export type FactoryRunStatus =
  | 'discovery'
  | 'running'
  | 'pending'
  | 'pending_approval'
  | 'awaiting_oracle_approval'
  | 'awaiting_logos_approval'
  | 'awaiting_genesis_approval'
  | 'paused'
  | 'cancelled'
  | 'archived'
  | 'complete'
  | 'stuck';

export const AWAITING_APPROVAL_STATUSES: FactoryRunStatus[] = [
  'pending_approval',
  'awaiting_oracle_approval',
  'awaiting_logos_approval',
  'awaiting_genesis_approval',
];

export const ARCHIVED_STATUSES: FactoryRunStatus[] = ['archived'];
export const TERMINAL_STATUSES: FactoryRunStatus[] = ['archived', 'complete', 'cancelled'];

export interface FactoryRun {
  id: string;
  status: FactoryRunStatus;
  currentStage: FactoryStage;
  customerId?: string;
  customerName?: string; // display-only; resolved from Bible when available
  userEmail?: string;
  createdAtMs?: number;
  updatedAtMs?: number;
  lastMessageSnippet?: string;
  unreadForFounder?: boolean;
  // Used by ThreadList filtering when the raw status doesn't map cleanly
  // (e.g. a run that's discovery + pending_approval simultaneously).
  awaitingApproval?: boolean;
}

export type ThreadSender =
  | 'Discovery'
  | 'Oracle'
  | 'Logos'
  | 'Genesis'
  | 'Orchestrator'
  | 'Founder'
  | 'System';

export interface ThreadMessage {
  id: string;
  runId: string;
  sender: ThreadSender;
  text: string;
  createdAtMs: number;
  // When true, the message is rendered as an inline pill, not a bubble
  // (e.g. "Oracle stage complete — awaiting approval").
  isStageTransition?: boolean;
}

export interface FeatureFlag {
  on_for_all: boolean;
  on_for_founder: boolean;
}

export type StageAction = 'approve' | 'reject' | 'pause' | 'archive';
